import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Collapse, Typography } from 'antd';
import { CaretDownOutlined } from '@ant-design/icons';
import { supportArticleCategories, supportArticleIndex, supportArticlePath } from './supportArticles';
import { parseSupportArticle, type ParsedSupportArticle, type SupportArticleSection } from './utils/supportArticleParser';
import SupportArticleInline from './components/SupportArticleInline';
import './Support.css';

const { Panel } = Collapse;
const { Text } = Typography;

export type CategorizedSupportArticle = ParsedSupportArticle & {
  categoryKey: string;
  categoryTitle: string;
  order: number;
};

type SupportArticleCategoryGroup = {
  key: string;
  title: string;
  articles: CategorizedSupportArticle[];
};

type SupportArticleResultsProps = {
  articles: CategorizedSupportArticle[];
  query?: string;
  activeSlug?: string;
  highlightedSlug?: string;
  displayMode?: 'browse' | 'search';
  defaultOpenCategories?: string[];
  emptyMessage?: string;
  openCategoryKeys?: string[];
  resultLabel?: string;
  showToolbar?: boolean;
  onOpenCategoryKeysChange?: (keys: string[]) => void;
  onActiveSlugChange?: (slug?: string) => void;
};

const areStringArraysEqual = (a: string[], b: string[]) => (
  a.length === b.length && a.every((value, index) => value === b[index])
);

const normalizeTitle = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const highlight = (text: string, query = ''): React.ReactNode => {
  const q = query.trim();
  if (!q) return text;
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'ig'));
  return (
    <>
      {parts.map((part, index) => (
        part.toLowerCase() === q.toLowerCase()
          ? <mark key={`${part}-${index}`}>{part}</mark>
          : <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      ))}
    </>
  );
};

export const loadSupportArticles = async (): Promise<CategorizedSupportArticle[]> => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    throw new Error('Support articles are unavailable in this environment.');
  }

  const results = await Promise.all(
    supportArticleIndex.map(async (entry) => {
      const response = await fetch(supportArticlePath(entry.file));
      if (!response.ok) {
        throw new Error(`Failed to load ${entry.file}`);
      }
      const text = await response.text();
      const parsed = parseSupportArticle(text, entry.slug);
      return {
        ...parsed,
        categoryKey: entry.categoryKey,
        categoryTitle: entry.categoryTitle,
        order: entry.order,
      };
    }),
  );

  return results.sort((a, b) => a.order - b.order);
};

const isRelatedSection = (section: SupportArticleSection) =>
  normalizeTitle(section.heading) === 'related articles';

const renderSection = (
  section: SupportArticleSection,
  index: number,
  titleToSlug: Map<string, string>,
  onRelatedClick: (slug?: string) => void,
) => {
  const related = isRelatedSection(section);
  const listItems = section.listItems.map((item, itemIndex) => {
    if (related) {
      const slug = titleToSlug.get(normalizeTitle(item));
      if (slug) {
        return (
          <li key={`${section.heading}-rel-${itemIndex}`}>
            <Button
              type="link"
              size="small"
              onClick={() => onRelatedClick(slug)}
              className="support-article-related-link"
            >
              {item}
            </Button>
          </li>
        );
      }
    }
    return (
      <li key={`${section.heading}-item-${itemIndex}`}>
        <SupportArticleInline text={item} />
      </li>
    );
  });

  return (
    <section key={`${section.heading}-${index}`} className="support-article-section">
      <h4>{section.heading}</h4>
      {section.paragraphs.map((paragraph, paragraphIndex) => (
        <p key={`${section.heading}-p-${paragraphIndex}`}>
          <SupportArticleInline text={paragraph} />
        </p>
      ))}
      {section.listItems.length > 0 && section.listType === 'ordered' && <ol>{listItems}</ol>}
      {section.listItems.length > 0 && section.listType !== 'ordered' && <ul>{listItems}</ul>}
    </section>
  );
};

const renderArticleBody = (
  article: CategorizedSupportArticle,
  titleToSlug: Map<string, string>,
  onRelatedClick: (slug?: string) => void,
) => (
  <div className="support-article-body">
    {article.sections.map((section, index) => (
      renderSection(section, index, titleToSlug, onRelatedClick)
    ))}
  </div>
);

export const SupportArticleResults: React.FC<SupportArticleResultsProps> = ({
  articles,
  query = '',
  activeSlug,
  highlightedSlug,
  displayMode = 'browse',
  defaultOpenCategories,
  emptyMessage = 'No articles match your search.',
  openCategoryKeys,
  resultLabel,
  showToolbar = false,
  onOpenCategoryKeysChange,
  onActiveSlugChange,
}) => {
  const [internalOpenCategories, setInternalOpenCategories] = useState<string[]>(() => defaultOpenCategories ?? []);
  const [localActiveSlug, setLocalActiveSlug] = useState<string | undefined>(activeSlug);
  const resolvedActiveSlug = activeSlug ?? localActiveSlug;
  const isControlled = openCategoryKeys !== undefined;
  const openCategories = openCategoryKeys ?? internalOpenCategories;

  const setOpenCategories = useCallback((updater: string[] | ((prev: string[]) => string[])) => {
    const next = typeof updater === 'function' ? updater(openCategories) : updater;
    if (areStringArraysEqual(next, openCategories)) {
      return;
    }
    if (isControlled) {
      onOpenCategoryKeysChange?.(next);
    } else {
      setInternalOpenCategories(next);
    }
  }, [isControlled, onOpenCategoryKeysChange, openCategories]);

  const titleToSlug = useMemo(() => {
    const map = new Map<string, string>();
    articles.forEach((article) => {
      map.set(normalizeTitle(article.title), article.slug);
    });
    return map;
  }, [articles]);

  const categoryGroups = useMemo<SupportArticleCategoryGroup[]>(() => {
    const grouped = new Map<string, CategorizedSupportArticle[]>();
    supportArticleCategories.forEach((category) => grouped.set(category.key, []));
    articles.forEach((article) => {
      const bucket = grouped.get(article.categoryKey);
      if (bucket) {
        bucket.push(article);
      }
    });
    return supportArticleCategories
      .map((category) => ({
        key: category.key,
        title: category.title,
        articles: grouped.get(category.key) || [],
      }))
      .filter((category) => category.articles.length > 0);
  }, [articles]);

  useEffect(() => {
    if (isControlled) {
      return;
    }
    if (defaultOpenCategories) {
      setOpenCategories(defaultOpenCategories);
      return;
    }
    if (query.trim()) {
      setOpenCategories(categoryGroups.map((category) => category.key));
    }
  }, [categoryGroups, defaultOpenCategories, isControlled, query, setOpenCategories]);

  useEffect(() => {
    if (!resolvedActiveSlug) return;
    const match = articles.find((article) => article.slug === resolvedActiveSlug);
    if (!match) {
      setLocalActiveSlug(undefined);
      onActiveSlugChange?.(undefined);
      return;
    }
    setOpenCategories((prev) => (
      prev.includes(match.categoryKey) ? prev : [...prev, match.categoryKey]
    ));
  }, [articles, onActiveSlugChange, resolvedActiveSlug, setOpenCategories]);

  useEffect(() => {
    if (!resolvedActiveSlug) return;
    window.setTimeout(() => {
      const target = document.querySelector(
        `[data-support-article-slug="${resolvedActiveSlug}"]`,
      ) as HTMLElement | null;
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      target?.focus({ preventScroll: true });
    }, 0);
  }, [openCategories, resolvedActiveSlug]);

  const setActive = (slug?: string) => {
    setLocalActiveSlug(slug);
    onActiveSlugChange?.(slug);
  };

  const handleRelatedClick = (slug?: string) => {
    if (!slug) return;
    setActive(slug);
    const target = document.getElementById('support-articles');
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleCategory = (key: string) => {
    setOpenCategories((prev) => (
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    ));
  };

  const isSearchMode = displayMode === 'search';

  if (categoryGroups.length === 0) {
    return <div className="support-state-panel"><Text type="secondary">{emptyMessage}</Text></div>;
  }

  if (isSearchMode) {
    return (
      <div className="support-article-results">
        {showToolbar && (
          <div className="support-article-toolbar">
            <span className="support-article-toolbar-label">
              {resultLabel || `${articles.length} result${articles.length === 1 ? '' : 's'}`}
            </span>
          </div>
        )}
        <div className="support-search-result-list">
          {articles.map((article) => {
            const isActive = article.slug === resolvedActiveSlug;
            const isHighlighted = article.slug === highlightedSlug;
            return (
              <article
                key={article.slug}
                className={[
                  'support-search-result',
                  isActive ? 'support-search-result--active' : '',
                  isHighlighted ? 'support-search-result--highlight' : '',
                ].filter(Boolean).join(' ')}
                data-support-article-slug={article.slug}
                tabIndex={-1}
              >
                <button
                  type="button"
                  className="support-search-result-button"
                  onClick={() => setActive(isActive ? undefined : article.slug)}
                  aria-expanded={isActive}
                >
                  <span className="support-search-result-meta">{article.categoryTitle}</span>
                  <span className="support-search-result-title">{highlight(article.title, query)}</span>
                  {article.summary && (
                    <span className="support-search-result-summary">{highlight(article.summary, query)}</span>
                  )}
                </button>
                {isActive && renderArticleBody(article, titleToSlug, handleRelatedClick)}
              </article>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="support-article-results">
      {showToolbar && (
        <div className="support-article-toolbar">
          <span className="support-article-toolbar-label">
            {resultLabel || `${articles.length} article${articles.length === 1 ? '' : 's'}`}
          </span>
          <span className="support-article-toolbar-actions">
            <button type="button" className="support-article-toolbar-button" onClick={() => setOpenCategories(categoryGroups.map((category) => category.key))}>
              Expand all
            </button>
            <button type="button" className="support-article-toolbar-button" onClick={() => setOpenCategories([])}>
              Collapse all
            </button>
          </span>
        </div>
      )}
      <div className="support-category-list">
        {categoryGroups.map((category) => {
          const isOpen = openCategories.includes(category.key);
          const activeKey = category.articles.some((article) => article.slug === resolvedActiveSlug)
            ? resolvedActiveSlug
            : undefined;
          return (
            <section key={category.key} className="support-category-section">
              <button
                type="button"
                className="support-category-header"
                onClick={() => toggleCategory(category.key)}
                aria-expanded={isOpen}
                aria-controls={`support-category-${category.key}`}
              >
                <span>
                  <span className="support-category-title">{category.title}</span>
                  <span className="support-category-count">
                    {category.articles.length} article{category.articles.length === 1 ? '' : 's'}
                  </span>
                </span>
                <CaretDownOutlined className={isOpen ? 'support-category-caret support-category-caret--open' : 'support-category-caret'} />
              </button>
              {isOpen && (
                <div id={`support-category-${category.key}`} className="support-category-body">
                  <Collapse
                    accordion
                    bordered={false}
                    activeKey={activeKey}
                    onChange={(key) => {
                      if (Array.isArray(key)) {
                        setActive(key[0]);
                      } else {
                        setActive(key || undefined);
                      }
                    }}
                    className="support-article-collapse"
                  >
                    {category.articles.map((article) => {
                      const isHighlighted = article.slug === highlightedSlug;
                      return (
                        <Panel
                          key={article.slug}
                          className={[
                            'support-article-panel',
                            isHighlighted ? 'support-article-panel--highlight' : '',
                          ].filter(Boolean).join(' ')}
                          header={
                            <div
                              className={[
                                'support-article-header',
                                isHighlighted ? 'support-article-header--highlight' : '',
                              ].filter(Boolean).join(' ')}
                              data-support-article-slug={article.slug}
                              tabIndex={-1}
                            >
                              <span className="support-article-title">{highlight(article.title, query)}</span>
                              {article.summary && (
                                <Text type="secondary">{highlight(article.summary, query)}</Text>
                              )}
                            </div>
                          }
                        >
                          {renderArticleBody(article, titleToSlug, handleRelatedClick)}
                        </Panel>
                      );
                    })}
                  </Collapse>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};

const SupportArticles: React.FC = () => {
  const [query, setQuery] = useState('');
  const [articles, setArticles] = useState<CategorizedSupportArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isActive = true;
    const load = async () => {
      try {
        const results = await loadSupportArticles();
        if (isActive) {
          setArticles(results);
          setLoading(false);
        }
      } catch {
        if (isActive) {
          setError('Unable to load support articles right now.');
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      isActive = false;
    };
  }, []);

  const filteredArticles = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter((article) => article.searchText.includes(q));
  }, [articles, query]);

  return (
    <section className="support-section-card" id="support-articles">
      <div className="support-section-heading">
        <div>
          <span className="support-kicker">Articles</span>
          <h2>Support Articles</h2>
          <p>Browse every help article by topic.</p>
        </div>
      </div>
      <div className="support-toolbar">
        <input
          className="support-native-search"
          placeholder="Search support articles"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search support articles"
        />
        <span className="support-result-pill">
          {loading ? 'Loading...' : `${filteredArticles.length} article${filteredArticles.length === 1 ? '' : 's'}`}
        </span>
      </div>
      {error && <Alert message={error} type="warning" showIcon />}
      {!error && !loading && (
        <SupportArticleResults
          articles={filteredArticles}
          query={query}
          displayMode={query.trim() ? 'search' : 'browse'}
          emptyMessage="No articles match your search."
        />
      )}
    </section>
  );
};

export default SupportArticles;
